import type { Item, List } from "../lib/api.js";
import { el, setText } from "../lib/dom.js";
import { titled } from "../lib/environment.js";
import { todayIso } from "../lib/format.js";
import { icon } from "../lib/icons.js";
import { hasNote, renderNote } from "../lib/markdown.js";
import { back, leave, listPath, navigate, opener, parseRoute, viewEditPath, viewPath, type Route } from "../lib/router.js";
import { DISCARD_EDITS, editorPanel } from "./editor.js";
import { ITEM_GONE } from "./list.js";
import { banner, messageOf, noteFacts, notFound, paintMeta, unreachable, type Screen, type Shell } from "./ui.js";

/**
 * One item's note, read on a page of its own (g77).
 *
 * The page is the item: its text as the heading, one line of facts, and the
 * note rendered from its markdown, with Edit and a way back and nothing else.
 * It is reached from View note in an item's menu, on a list or on Today and
 * Upcoming, and goes back to whichever screen opened it; a copied address
 * goes back to the item's list (`leave`).
 *
 * Edit is the same Details panel a list opens, at the page's own `/edit`
 * address, so Cancel, Escape, the backdrop and Back each guard a draft
 * exactly as they do over a list. The page follows the item as other devices
 * and agents change it, repainting only what changed, and leaves for its
 * opener with a notice when the item or its note goes.
 */

/** Exported so the tests pin the words. */
export const NOTE_WORDS = {
  back: (place: string) => `Back to ${place}`,
  edit: "Edit",
  deleted: "This item was deleted.",
  emptied: "This item no longer has a note.",
  loading: "Loading note",
  notDownloaded: "Note not downloaded yet",
};

/** A note's `#` on this page: one below the page's own `h1`, the item's text. */
const NOTE_HEADING_LEVEL = 2;

type NoteRoute = Extract<Route, { name: "note" }>;

export function noteViewerScreen(shell: Shell, route: NoteRoute): Screen {
  const { store } = shell;
  const itemId = route.item;
  /** The list the address names, moved on when the item moves list. */
  let listId = route.id;
  const node = el("section", { class: "screen note-viewer" });
  const title = document.title;

  let page: ReturnType<typeof build> | undefined;
  /** Whether the item was ever on this page: a later absence is a deletion, not a wrong address. */
  let seen = false;
  /**
   * Whether the page is on screen. It is built before the outgoing screen
   * is disposed, which clears the panel, and before the router has finished
   * committing the navigation that made it: until `update`, it only paints.
   */
  let mounted = false;
  /** Whether the address asks for Edit, and whether the panel is up. */
  let wanted = false;
  let editing = false;
  /** Whether this page pushed the `/edit` entry, so closing it is Back. */
  let pushed = false;
  /** On its way out: nothing more is painted or decided. */
  let gone = false;

  /** Where Back goes, in words: the opener's name, or the item's list. */
  function place(): string {
    const from = opener();
    const to = from ? parseRoute(from) : undefined;
    if (to?.name === "today") return "Today";
    if (to?.name === "upcoming") return "Upcoming";
    if (to?.name === "lists") return "Lists";
    const id = to?.name === "list" ? to.id : listId;
    return store.list(id)?.name ?? "list";
  }

  function goBack(): void {
    leave(listPath(listId));
  }

  function build() {
    const backButton = el("button", {
      class: "note-viewer-back",
      attrs: { type: "button" },
      children: [icon("back"), el("span")],
      on: { click: goBack },
    });
    const heading = el("h1", { class: "note-viewer-title" });
    const facts = el("p", { class: "item-meta note-viewer-facts" });
    const status = el("div", { class: "status" });
    const edit = el("button", {
      class: "secondary note-viewer-edit",
      attrs: { type: "button" },
      children: [icon("edit"), el("span", { text: NOTE_WORDS.edit })],
      on: { click: openEdit },
    });
    let body = renderNote("", { headingLevel: NOTE_HEADING_LEVEL });
    let shownNote: string | undefined;
    node.replaceChildren(
      backButton,
      el("div", { class: "note-viewer-head", children: [heading, facts] }),
      status,
      edit,
      body,
    );
    return {
      heading,
      edit,
      setStatus(child: Node | undefined) {
        status.replaceChildren(...(child ? [child] : []));
      },
      /** Each block is replaced only when what it shows changed, so a selection survives a sync. */
      paint(item: Item, list: List) {
        setText(backButton.lastElementChild!, NOTE_WORDS.back(place()));
        setText(heading, item.text);
        paintMeta(facts, noteFacts(item, list.kind, todayIso()));
        const note = item.note ?? "";
        if (note !== shownNote) {
          shownNote = note;
          const next = renderNote(note, { headingLevel: NOTE_HEADING_LEVEL });
          body.replaceWith(next);
          body = next;
        }
        document.title = titled(list.name);
      },
    };
  }

  /** The item and its list as the store has them, or undefined while either is missing. */
  function current(): { item: Item; list: List } | undefined {
    const item = store.item(itemId);
    const list = item ? store.list(item.listId) : undefined;
    return item && list ? { item, list } : undefined;
  }

  /**
   * The page, painted from the store: the item's page when it is here with a
   * note, and otherwise loading, not downloaded, not found, or on its way back
   * to the opener. With the panel up, an emptied note is painted and the page
   * leaves once the panel comes down, since that is the person's own Save.
   */
  function render(): void {
    if (gone) return;
    const now = current();
    if (now && now.item.listId !== listId) {
      if (!mounted) return;
      // Moved to another list: the same page, at the address that names it.
      listId = now.item.listId;
      navigate(wanted ? viewEditPath(listId, itemId) : viewPath(listId, itemId), { replace: true, local: true });
      return;
    }
    if (now && (hasNote(now.item.note) || editing)) {
      seen = true;
      page ??= build();
      page.paint(now.item, now.list);
      if (mounted && wanted && !editing) present(now.item, now.list);
      return;
    }
    if (now || seen) {
      if (mounted) depart(now ? "emptied" : "deleted");
      return;
    }
    if (store.status() === "loading") {
      node.replaceChildren(el("h1", { class: "visually-hidden", text: NOTE_WORDS.loading }), el("p", { class: "muted", text: "Loading…" }));
      return;
    }
    if (store.status() === "unreachable") {
      node.replaceChildren(el("h1", { class: "visually-hidden", text: NOTE_WORDS.notDownloaded }), unreachable(store));
      return;
    }
    node.replaceChildren(...notFound());
  }

  /**
   * Back to the opener, saying why. The panel comes down first, and the page
   * leaves from the `render` that follows: a deletion under a changed draft
   * asks, as it does over a list, since Keep editing is there to copy it.
   */
  function depart(reason: "deleted" | "emptied"): void {
    if (editing) {
      if (!shell.detail.dirty()) dismiss();
      else if (!shell.detail.asking()) {
        shell.detail.confirm({
          ...ITEM_GONE,
          keep: { label: DISCARD_EDITS.keep, onChoose: () => shell.detail.resume() },
          act: { label: DISCARD_EDITS.discard, onChoose: dismiss },
        });
      }
      return;
    }
    if (wanted) {
      // Asked for Edit with nothing to edit: the address goes back to the
      // page first, and the page leaves from there.
      navigate(viewPath(listId, itemId), { replace: true, local: true });
      return;
    }
    gone = true;
    goBack();
    shell.announce(NOTE_WORDS[reason]);
  }

  function openEdit(): void {
    const replace = shell.detail.persistent();
    navigate(viewEditPath(listId, itemId), { replace });
    if (!replace && editing) pushed = true;
  }

  /** The person closed the panel: undo the navigation that opened it. */
  function dismiss(): void {
    if (pushed) {
      pushed = false;
      back();
      return;
    }
    navigate(viewPath(listId, itemId), { replace: true, local: true });
  }

  function present(item: Item, list: List): void {
    editing = true;
    const editor = editorPanel(shell, item, list, {
      onDismiss: dismiss,
      onResult: (error) => {
        if (error === undefined) return page?.setStatus(undefined);
        const message = messageOf(error);
        page?.setStatus(banner(message));
        shell.announce(message);
      },
    });
    shell.detail.present(editor.node, {
      label: `Edit ${item.text}`,
      title: "Edit item",
      onDismiss: dismiss,
      returnFocus: () => page?.edit,
      guard: editor.guard,
    });
  }

  /** What the address asks for, applied: the panel up or down. */
  function select(open: boolean): void {
    wanted = open;
    if (!open && editing) {
      editing = false;
      pushed = false;
      shell.detail.clear();
    }
    render();
  }

  const stopItems = store.onAllItems((change) => { if (change.id === itemId) render(); });
  const stopLists = store.onLists(() => { if (page && !gone) render(); });
  const stopStatus = store.onStatus(() => { if (!page) render(); });
  // An owner's list with a working link may change without this device
  // doing anything, and is kept fresh while its note is open (g71).
  let watching = { id: listId, stop: store.watchList(listId) };
  const watch = (): void => {
    if (watching.id === listId) return;
    watching.stop();
    watching = { id: listId, stop: store.watchList(listId) };
  };

  render();

  return {
    node,
    update(next: Route) {
      if (next.name !== "note" || next.item !== itemId) return;
      mounted = true;
      listId = next.id;
      watch();
      select(Boolean(next.editing));
    },
    dispose() {
      gone = true;
      stopItems();
      stopLists();
      stopStatus();
      watching.stop();
      shell.detail.clear();
      document.title = title;
    },
  };
}

/** View note, from an item's menu: the page, with this screen recorded as where Back goes. */
export function openNote(listId: string, itemId: string): void {
  navigate(viewPath(listId, itemId), { opener: true });
}
